import * as React from 'react';

import {Button} from 'shells/button/button';
import {TinyGreyCard} from 'shells/card/tiny-grey-card';
import {colors} from 'shells/colors/colors';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {AddressCardIcon} from 'shells/icon/svg/address-card-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
// @ts-expect-error - imported from .js/.jsx file
import {IndustryIcon} from 'shells/icon/svg/industry-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {MergeTagOption, View} from './add-merge-field';
import {Fallback} from './fallback';

import styles from './add-merge-field.module.css';

type Props = {
    address: string | null | undefined;
    isLoading: boolean;
    settingsLink: string | React.ReactNode;
    selectedMergeTag: MergeTagOption | null | undefined;
    setSelectedMergeTag: (arg1: MergeTagOption) => void;
    fallbackValue: string;
    setFallbackValue: (arg1: string) => void;
    setCurrentView: (arg1: View) => void;
};

export function StandardTags(props: Props) {
    const options: MergeTagOption[] = [
        {
            label: 'First name',
            value: 'firstName',
            fallback: {
                defaultValue: 'friend',
                placeholder: 'e.g. Friend',
            },
            details: <div>Displays the recipient’s first name</div>,
            icon: ContactIcon,
        },
        {
            label: 'Last name',
            value: 'lastName',
            fallback: null,
            details: <div>Displays the recipient’s last name</div>,
            icon: ContactIcon,
        },
        {
            label: 'Full name',
            value: 'name',
            fallback: {
                defaultValue: 'friend',
                placeholder: 'e.g. Friend',
            },
            details: <div>Displays the recipient’s full name</div>,
            icon: ContactIcon,
        },
        {
            label: 'Company name',
            value: 'companyName',
            fallback: {
                defaultValue: 'your company',
                placeholder: 'e.g. Your company',
            },
            details: <div>Displays the company name related to the recipient</div>,
            icon: AccountIcon,
        },
        {
            label: 'Company industry',
            value: 'companyIndustry',
            fallback: {
                defaultValue: 'your industry',
                placeholder: 'e.g. Your industry',
            },
            details: <div>Displays the company industry related to the recipient</div>,
            icon: IndustryIcon,
        },
        {
            label: 'Mailing address',
            value: 'mailingAddress',
            fallback: null,
            details: (
                <div className='font-bold'>
                    {props.address ? <div>{props.address}</div> : undefined}
                </div>
            ),
            settingsLinkText:
                props.address || props.isLoading ? (
                    <div>Admins can update this in {props.settingsLink}.</div>
                ) : (
                    <div>Admins can add a mailing address in {props.settingsLink}.</div>
                ),
            icon: AddressCardIcon,
        },
    ];

    return (
        <>
            <StackLegacy spacing={8}>
                <div className={styles['grid-container']}>
                    {options.map((option) => {
                        const Icon = option.icon || ContactIcon;

                        return (
                            <TinyGreyCard
                                key={option.value}
                                // @ts-expect-error
                                icon={<Icon fill={colors.grey} size={24} />}
                                text={option.label}
                                onClick={() => props.setSelectedMergeTag(option)}
                            />
                        );
                    })}
                </div>
                {props.selectedMergeTag ? (
                    <>
                        {props.selectedMergeTag.details ? (
                            <div className={styles.details}>{props.selectedMergeTag.details}</div>
                        ) : undefined}
                        {props.selectedMergeTag.value === 'mailingAddress' &&
                        props.selectedMergeTag.settingsLinkText ? (
                            <div className={styles.details}>
                                All marketing emails must include a valid mailing address.
                                {props.selectedMergeTag.settingsLinkText}
                            </div>
                        ) : (
                            <Fallback
                                fallbackValue={props.fallbackValue}
                                setFallbackValue={props.setFallbackValue}
                                placeholder={
                                    props.selectedMergeTag.fallback
                                        ? props.selectedMergeTag.fallback.placeholder
                                        : undefined
                                }
                                label={props.selectedMergeTag.label.toLocaleLowerCase()}
                            />
                        )}
                    </>
                ) : undefined}
                <div className={styles.button}>
                    <Button
                        variant='text-primary'
                        onClick={() => props.setCurrentView('custom')}
                        noPadding={true}
                    >
                        Choose a custom field
                    </Button>
                </div>
            </StackLegacy>
        </>
    );
}
